/**
 * A zona da previsão na linha do tempo — funções puras (testadas em
 * `zona.test.ts`); `ZonaDaPrevisao.tsx` só desenha.
 *
 * A zona fica à direita do eixo das imagens, como o futuro no Windy e no
 * RainViewer: a ponta esquerda é a imagem de onde a previsão parte (a mais
 * nova que tem previsão), a direita, a maior antecedência. O eixo de dentro é
 * de antecedência, não de relógio: de 0 à maior antecedência do arquivo na
 * tela (ou do índice). A hora em que cada contorno vale aparece no balão do
 * mouse, no relógio da barra e no aviso sobre o mapa.
 */
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { antecedenciaValida, rotuloAntecedencia } from './tempo';

/** O selo de novidade da zona: some no primeiro `verPrevisao`. */
export const NOVIDADE_DA_ZONA = { id: 'previsao-na-barra', ate: '2026-11-30' } as const;

/** As antecedências que a máquina publica (30 min a 3 h), enquanto o índice não chega. */
export const ANTECEDENCIAS_PADRAO: readonly number[] = [30, 60, 90, 120, 150, 180];

/** Posição (0–1) da antecedência `h` (min) na zona que vai de 0 a `maxH`. */
export function posicaoNaZona(h: number, maxH: number): number {
  if (!(maxH > 0) || !Number.isFinite(h)) return 0;
  return Math.max(0, Math.min(1, h / maxH));
}

/**
 * A antecedência disponível mais próxima da posição `p` (0–1). À esquerda da
 * primeira dá a primeira: um toque na zona é sempre um pedido de previsão,
 * nunca de "agora" (a observação é o eixo das imagens, ao lado). Sem nenhuma
 * disponível, 0.
 */
export function antecedenciaNaPosicao(p: number, disponiveis: readonly number[], maxH: number): number {
  const validas = disponiveis.filter((a) => a > 0);
  if (!validas.length) return 0;
  const h = Math.max(0, Math.min(1, Number.isFinite(p) ? p : 0)) * Math.max(0, maxH);
  return antecedenciaValida(Math.max(h, validas[0]), validas);
}

/**
 * Onde o instante `agora` cai na zona (0–1), ou null fora dela. A previsão
 * parte de uma imagem de 10–25 min atrás: "agora" costuma cair perto de +20.
 */
export function agoraNaZona(agora: number, origem: number | null, maxH: number): number | null {
  if (origem === null || !(maxH > 0)) return null;
  const p = (agora - origem) / (maxH * MINUTE_MS);
  return p >= 0 && p <= 1 ? p : null;
}

export interface RotuloDaZona {
  h: number;
  texto: string;
}

/**
 * Os rótulos sob a zona. Desktop: as horas cheias ("+1 h", "+2 h", "+3 h") e
 * a maior antecedência, se ela não cai numa hora cheia (arquivos antigos iam
 * até +1 h; um passo de 45 min iria a +4h30). Celular (56 px): só a
 * antecedência na tela, ou nenhum.
 */
export function rotulosDaZona(passos: readonly number[], compacto: boolean, ativa = 0): RotuloDaZona[] {
  if (compacto) return ativa > 0 ? [{ h: ativa, texto: rotuloAntecedencia(ativa) }] : [];
  const validos = passos.filter((a) => a > 0);
  const maior = validos[validos.length - 1];
  const escolhidos = validos.filter((a) => a % 60 === 0 || a === maior);
  return escolhidos.map((h) => ({ h, texto: rotuloAntecedencia(h) }));
}

/** O que o leitor de tela diz da zona: "+1h30, válida às 16:30, a partir da imagem das 15:00". */
export function textoDaZona(h: number, origem: number | null): string {
  if (h <= 0) return origem === null ? 'Fora da previsão' : `Fora da previsão; a mais nova parte da imagem das ${formatTimeBrt(origem)}`;
  if (origem === null) return rotuloAntecedencia(h);
  return `${rotuloAntecedencia(h)}, válida às ${formatTimeBrt(origem + h * MINUTE_MS)}, a partir da imagem das ${formatTimeBrt(origem)}`;
}

/**
 * O cursor do tempo está na zona: a previsão ligada, uma antecedência na tela
 * (passo 0 do fluxo e "agora" são a observação, no eixo das imagens) e a
 * imagem exibida é a origem da zona. Previsão ligada num quadro mais velho
 * (pelo painel) é verificação: o cursor fica no eixo das imagens.
 */
export function estaNaZona(previsao: boolean, naTela: number, index: number, origem: number): boolean {
  return previsao && naTela > 0 && origem >= 0 && index === origem;
}
